import { useState, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const projectTypes = [
  { value: 'refection', label: 'form_type_refection', icon: 'ri-home-4-line' },
  { value: 'repair', label: 'form_type_repair', icon: 'ri-tools-line' },
  { value: 'inspection', label: 'form_type_inspection', icon: 'ri-search-line' },
  { value: 'flat', label: 'form_type_flat', icon: 'ri-building-line' },
  { value: 'deneigement', label: 'form_type_deneigement', icon: 'ri-snowy-line' },
  { value: 'other', label: 'form_type_other', icon: 'ri-question-line' },
];

const materials = [
  { value: 'bardeau', label: 'form_material_bardeau' },
  { value: 'tole', label: 'form_material_tole' },
  { value: 'membrane', label: 'form_material_membrane' },
  { value: 'dontknow', label: 'form_material_dontknow' },
];

const delays = [
  { value: 'urgent', label: 'form_delay_urgent' },
  { value: 'short', label: 'form_delay_short' },
  { value: 'medium', label: 'form_delay_medium' },
  { value: 'flexible', label: 'form_delay_flexible' },
];

export default function QuoteForm() {
  const { t } = useTranslation('soumission');
  const [step, setStep] = useState(1);
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    projectType: '',
    material: '',
    delay: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    const form = formRef.current;
    if (form) {
      const data = new FormData(form);
      fetch(form.action, {
        method: form.method,
        body: data,
        headers: { Accept: 'application/json' },
      }).finally(() => {
        if (window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/merci');
        }
      });
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg">
      <h3 className="font-manrope font-bold text-slate-primary text-lg md:text-xl text-center mb-6">
        {t('form_title')}
      </h3>

      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`flex-1 h-1 rounded-full ${step >= 1 ? 'bg-copper' : 'bg-soft-dark'}`} />
        <span className={`text-sm font-medium ${step >= 1 ? 'text-copper' : 'text-gray-400'}`}>1</span>
        <div className={`flex-1 h-1 rounded-full ${step >= 2 ? 'bg-copper' : 'bg-soft-dark'}`} />
        <span className={`text-sm font-medium ${step >= 2 ? 'text-copper' : 'text-gray-400'}`}>2</span>
      </div>

      <form
        ref={formRef}
        action="https://readdy.ai/api/form/d84brj66qilughrrc85g"
        method="POST"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {step === 1 && (
          <>
            <p className="text-sm font-semibold text-slate-primary mb-3">{t('form_step_1')}</p>

            {/* Project type */}
            <div>
              <label className="block text-sm font-medium text-slate-primary mb-2">{t('form_type_label')}</label>
              <div className="grid grid-cols-2 gap-2">
                {projectTypes.map((pt) => (
                  <button
                    key={pt.value}
                    type="button"
                    onClick={() => handleChange('projectType', pt.value)}
                    className={`flex flex-col items-center gap-1.5 p-4 min-h-[48px] rounded-lg border text-sm transition-all ${
                      formData.projectType === pt.value
                        ? 'border-copper bg-copper/5 text-copper font-semibold'
                        : 'border-soft-dark hover:border-copper/40 text-slate-primary'
                    }`}
                  >
                    <i className={`${pt.icon} text-lg`} />
                    <span className="text-sm">{t(pt.label)}</span>
                  </button>
                ))}
              </div>
              <input type="hidden" name="projectType" value={formData.projectType} />
            </div>

            {/* Material */}
            <div>
              <label className="block text-sm font-medium text-slate-primary mb-2">{t('form_material_label')}</label>
              <div className="grid grid-cols-2 gap-2">
                {materials.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => handleChange('material', m.value)}
                    className={`p-4 min-h-[48px] rounded-lg border text-sm transition-all ${
                      formData.material === m.value
                        ? 'border-copper bg-copper/5 text-copper font-semibold'
                        : 'border-soft-dark hover:border-copper/40 text-slate-primary'
                    }`}
                  >
                    {t(m.label)}
                  </button>
                ))}
              </div>
              <input type="hidden" name="material" value={formData.material} />
            </div>

            {/* Delay */}
            <div>
              <label className="block text-sm font-medium text-slate-primary mb-2">{t('form_delay_label')}</label>
              <div className="grid grid-cols-2 gap-2">
                {delays.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => handleChange('delay', d.value)}
                    className={`p-4 min-h-[48px] rounded-lg border text-sm transition-all ${
                      formData.delay === d.value
                        ? 'border-copper bg-copper/5 text-copper font-semibold'
                        : 'border-soft-dark hover:border-copper/40 text-slate-primary'
                    }`}
                  >
                    {t(d.label)}
                  </button>
                ))}
              </div>
              <input type="hidden" name="delay" value={formData.delay} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm font-semibold text-slate-primary mb-3">{t('form_step_2')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sm-name" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_name_label')}
                </label>
                <input
                  id="sm-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_name_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="sm-phone" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_phone_label')}
                </label>
                <input
                  id="sm-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_phone_placeholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sm-email" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_email_label')}
                </label>
                <input
                  id="sm-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_email_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="sm-address" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_address_label')}
                </label>
                <input
                  id="sm-address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_address_placeholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="sm-message" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_message_label')}
              </label>
              <textarea
                id="sm-message"
                name="message"
                rows={3}
                maxLength={500}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors resize-none"
                placeholder={t('form_message_placeholder')}
              />
              <p className="text-gray-400 text-xs mt-1 text-right">
                {formData.message.length}{t('form_char_count')}
              </p>
            </div>
          </>
        )}

        <input
          type="text"
          name="_gotcha"
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {/* Buttons */}
        <div className="flex items-center gap-3 pt-2">
          {step === 2 && (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="px-5 py-3.5 border border-soft-dark rounded-md text-slate-primary text-sm font-medium hover:border-copper hover:text-copper transition-all whitespace-nowrap"
            >
              {t('form_back')}
            </button>
          )}
          <button
            type="submit"
            className="flex-1 bg-copper hover:bg-copper-dark text-white font-bold py-3.5 rounded-md text-base transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {step === 1 ? t('form_next') : t('form_submit')}
            <i className={`ri-${step === 1 ? 'arrow-right' : 'send'}-line`} />
          </button>
        </div>
      </form>

      <p className="text-center text-gray-400 text-sm mt-4">
        {t('form_reassurance')}
      </p>
    </div>
  );
}